import {
    openModal,
    showSpinner,
    hideSpinner,
} from "@sitecorecomponents/BasicContent/Modal";
import { hasValidToken } from "@sitecorecomponents/Users/Store/tokenstore";
import { openLogin } from "@sitecorecomponents/Users/Login/login";
import { openSignup } from "@sitecorecomponents/Users/Signup/signup";
import {
    createEnrichmentIsland,
    getRenderedClietSideProps,
} from "@/utils/islands";
import { fetchApi } from "@/utils/fetcher";
import { readServiceResult } from "@sitecorecomponents/Users/authApi";

const modalName = "apply-open-house";
const createApplicationPath =
    "/api/usermanagement/advertapplications/createuseradvertapplication";
const myApplicationsPath =
    "/api/usermanagement/advertapplications/getuseradvertapplications";

// The API allows one application per advert - a second one is refused as a conflict - so the
// modal picks a single open house and locks itself once the user has applied. The exception is
// an application made while the advert had no open houses: that one can still get a time.
type MyApplication = {
    residenceAdvertId: string;
    preferredOpenHouses: { id: string }[] | null;
};

type Application = {
    applied: boolean;
    openHouseId?: string;
};

type OpenHouseSlot = {
    id: string;
    label: string;
};

type ApplyToOpenHouseProps = {
    advertId?: string;
    location?: string;
    slots?: OpenHouseSlot[];
};

// Guids travel as strings through both Sitecore and the API - do not assume the same casing.
function sameId(a?: string | null, b?: string | null): boolean {
    return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

/// The user's application on this advert, and the open house it picked - none when it was made
/// without a time.
async function fetchApplication(advertId: string): Promise<Application> {
    let response: Response;
    try {
        response = await fetchApi(myApplicationsPath);
    } catch (err) {
        console.log("Error while looking up open house applications", err);
        return { applied: false };
    }

    const result = await readServiceResult<MyApplication[]>(response);
    const application = result?.data?.find((advert) =>
        sameId(advert.residenceAdvertId, advertId),
    );
    return {
        applied: !!application,
        openHouseId: application?.preferredOpenHouses?.[0]?.id,
    };
}

function showState(root: HTMLElement, state: string) {
    root.querySelectorAll<HTMLElement>("[data-state]").forEach((elm) => {
        elm.hidden = elm.dataset.state !== state;
    });
}

function renderSlots(
    container: HTMLElement,
    slots: OpenHouseSlot[],
    appliedOpenHouseId: string | undefined,
    onChange: () => void,
) {
    slots.forEach((slot, index) => {
        const applied = sameId(slot.id, appliedOpenHouseId);

        const input = document.createElement("input");
        input.type = "radio";
        input.name = modalName;
        input.id = `${modalName}-${index}`;
        input.value = slot.id;
        input.checked = applied;
        // Already applied: the pick stays visible, but nothing can be picked again.
        input.disabled = !!appliedOpenHouseId;
        input.addEventListener("change", onChange);

        const text = document.createElement("span");
        text.textContent = slot.label;

        const state = applied
            ? "font-semibold"
            : input.disabled
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer";

        const label = document.createElement("label");
        label.className = `flex items-center gap-4 ${state}`;
        label.htmlFor = input.id;
        label.append(input, text);

        container.appendChild(label);
    });
}

function renderLocation(root: HTMLElement, location?: string) {
    const row = root.querySelector<HTMLElement>("[data-open-house-location]");
    const text = row?.querySelector<HTMLElement>(
        "[data-open-house-location-text]",
    );
    if (!row || !text) {
        return;
    }

    text.textContent = location ?? "";
    row.hidden = !location;
}

function selectedId(root: HTMLElement): string | null {
    const checked = root.querySelector<HTMLInputElement>(
        "[data-open-house-slots] input:checked",
    );
    return checked?.value ?? null;
}

async function submit(
    root: HTMLElement,
    advertId: string,
    openHouseIds: string[],
    successState: string,
) {
    let response: Response;
    try {
        showSpinner();
        response = await fetchApi(createApplicationPath, {
            method: "POST",
            body: JSON.stringify({
                residenceAdvertId: advertId,
                openHouseIds,
            }),
        });
    } catch (err) {
        console.log("Error while submitting open house application", err);
        showState(root, "error");
        return;
    } finally {
        hideSpinner();
    }

    // 200 with isSuccess false is how a refusal arrives - a duplicate application, say.
    const result = await readServiceResult(response);
    showState(root, result?.isSuccess ? successState : "error");
}

function wireAnonymousState(root: HTMLElement, props: ApplyToOpenHouseProps) {
    const openAndClose = (selector: string, open: () => void) =>
        root
            .querySelector<HTMLButtonElement>(selector)
            ?.addEventListener("click", () => {
                // Same handover as the login <-> signup links: close ours before opening theirs.
                root.closest("dialog")?.close();
                open();
            });

    // openLogin no longer navigates away, so it resolves once its modal closes - if the user
    // got a token, pick the apply flow back up where they left it.
    openAndClose("[data-open-login]", async () => {
        if (await openLogin()) {
            void openApplyModal(props);
        }
    });
    // Signup leaves the user logged in without navigating away, so resume the same way.
    openAndClose("[data-open-signup]", async () => {
        if (await openSignup()) {
            void openApplyModal(props);
        }
    });
}

function wireSelectState(
    root: HTMLElement,
    props: ApplyToOpenHouseProps,
    application: Application,
) {
    const container = root.querySelector<HTMLElement>(
        "[data-open-house-slots]",
    );
    const submitButton = root.querySelector<HTMLButtonElement>("[data-submit]");
    if (!container || !submitButton || !props.advertId) {
        return;
    }

    const syncSubmitButton = () => {
        submitButton.disabled =
            !!application.openHouseId || selectedId(root) === null;
    };

    renderSlots(
        container,
        props.slots ?? [],
        application.openHouseId,
        syncSubmitButton,
    );
    renderLocation(root, props.location);
    syncSubmitButton();

    submitButton.addEventListener("click", () => {
        const openHouseId = selectedId(root);
        if (!openHouseId || !props.advertId) {
            return;
        }

        submitButton.disabled = true;
        submit(root, props.advertId, [openHouseId], "success");
    });
}

async function openApplyModal(props: ApplyToOpenHouseProps) {
    const loggedIn = hasValidToken();
    // Looked up before the modal opens, so the state it lands on is the final one.
    let application: Application = { applied: false };
    if (loggedIn && props.advertId) {
        showSpinner();
        application = await fetchApplication(props.advertId);
        hideSpinner();
    }

    openModal({
        name: modalName,
        insertContent: (body) => {
            const root =
                body.querySelector<HTMLElement>(
                    "[data-apply-open-house-modal]",
                ) ?? body;

            if (!loggedIn) {
                showState(root, "anonymous");
                wireAnonymousState(root, props);
                return;
            }

            // No upcoming open houses: the click itself is the application, there is nothing to
            // pick - apply straight away, or just confirm when the user already has.
            if (!props.slots?.length) {
                if (application.applied) {
                    showState(root, "interest-success");
                } else if (props.advertId) {
                    showState(root, "");
                    void submit(root, props.advertId, [], "interest-success");
                } else {
                    showState(root, "error");
                }
                return;
            }

            showState(root, "select");
            wireSelectState(root, props, application);
        },
    });
}

export function initApplyToOpenHouseIsland() {
    createEnrichmentIsland('[app="apply-open-house"]', (root) => {
        const props = getRenderedClietSideProps(root) as ApplyToOpenHouseProps;

        root.querySelector<HTMLButtonElement>(
            "[data-apply-open-house]",
        )?.addEventListener("click", () => void openApplyModal(props));
    });
}
